import React, { FC } from 'react';
import { Input, InputNumber } from 'antd';
import type { InputProps, InputNumberProps } from 'antd';
import { useController, UseControllerProps, FieldValues, Path, useFormContext } from 'react-hook-form';
import clsx from 'clsx';
import Label from '../Label';
import FieldError from '../FieldError';
import styles from './inputField.module.scss';
import { InputFieldType } from '../../../enums/inputFieldTypes';
import ConditionalRender from '../ConditionalRender';
import { InputStyle } from '../../../enums/inputEnums';

export interface InputFieldProps<TFieldValues extends FieldValues = FieldValues, TName extends Path<TFieldValues> = Path<TFieldValues>> extends Pick<
  UseControllerProps<TFieldValues, TName>,
  'name' | 'rules' | 'defaultValue'
> {
  type: InputFieldType;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  addonBefore?: React.ReactNode;
  addonAfter?: React.ReactNode;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  allowClear?: boolean;
  maxLength?: number;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  rows?: number;
  showCount?: boolean;
  visibilityToggle?: boolean;
  noResize?: boolean;
  autoSize?: boolean | { minRows: number; maxRows: number };
  min?: number;
  max?: number;
  precision?: number;
  formatter?: InputNumberProps['formatter'];
  onNumberChange?: (value: number | string | null) => void;
  className?: string;
  labelClassName?: string;
  inputClassName?: string;
  errorClassName?: string;
  infoPopover?: React.ReactNode;
  control?: UseControllerProps<TFieldValues, TName>['control'];
  inputStyle?: InputStyle;
}

const { Password, TextArea } = Input;

const InputField: FC<InputFieldProps & Omit<InputProps, 'type' | 'prefix' | 'name'>> = (props) => {
  const {
    name,
    control: controlProp,
    rules,
    defaultValue,
    type,
    label,
    placeholder,
    disabled,
    required,
    addonBefore,
    addonAfter,
    prefix,
    suffix,
    allowClear,
    maxLength,
    onBlur,
    rows,
    showCount,
    visibilityToggle = true,
    noResize,
    autoSize,
    min,
    max,
    precision,
    formatter,
    onNumberChange,
    className,
    labelClassName,
    inputClassName,
    errorClassName,
    infoPopover,
    inputStyle = InputStyle.DEFAULT,
    ...rest
  } = props;

  const contextMethods = useFormContext();
  const resolvedControl = controlProp ?? contextMethods?.control;

  if (!resolvedControl) {
    throw new Error('InputField must be used inside a <Form> or receive explicit control prop (required for nested forms)');
  }

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control: resolvedControl,
    rules: required ? { required: 'is required', ...rules } : rules,
    defaultValue,
  });

  const hasError = Boolean(error);

  return (
    <div className={clsx(styles.inputContainer, className)}>
      <Label label={label} required={required} infoPopover={infoPopover} className={labelClassName} />
      <div
        className={clsx({
          [styles.defaultStyle]: inputStyle === InputStyle.DEFAULT,
          [styles.loginStyle]: inputStyle === InputStyle.LOGIN,
          [styles.borderedStyle]: inputStyle === InputStyle.BORDERED,
        })}
      >
        <ConditionalRender visible={type === InputFieldType.TEXT}>
          <Input
            {...field}
            placeholder={placeholder}
            disabled={disabled}
            status={hasError ? 'error' : undefined}
            addonBefore={addonBefore}
            addonAfter={addonAfter}
            prefix={prefix}
            suffix={suffix}
            allowClear={allowClear}
            maxLength={maxLength}
            onBlur={(e) => {
              field.onBlur();
              onBlur?.(e);
            }}
            className={inputClassName}
            {...rest}
          />
        </ConditionalRender>
        <ConditionalRender visible={type === InputFieldType.PASSWORD}>
          <Password
            {...field}
            placeholder={placeholder}
            disabled={disabled}
            status={hasError ? 'error' : undefined}
            onBlur={(e) => {
              field.onBlur();
              onBlur?.(e);
            }}
            className={clsx(inputClassName, styles.passwordInput)}
            visibilityToggle={visibilityToggle}
            {...rest}
          />
        </ConditionalRender>
        <ConditionalRender visible={type === InputFieldType.TEXTAREA}>
          <TextArea
            {...field}
            placeholder={placeholder}
            disabled={disabled}
            status={hasError ? 'error' : undefined}
            rows={rows ?? 4}
            maxLength={maxLength}
            showCount={showCount}
            autoSize={autoSize}
            className={clsx({ [styles.noResize]: noResize }, inputClassName, styles.regularInput)}
          />
        </ConditionalRender>
        <ConditionalRender visible={type === InputFieldType.NUMBER}>
          <InputNumber
            {...field}
            placeholder={placeholder}
            disabled={disabled}
            status={hasError ? 'error' : undefined}
            min={min}
            max={max}
            precision={precision}
            formatter={formatter}
            addonBefore={addonBefore}
            addonAfter={addonAfter}
            prefix={prefix as string}
            onChange={(val) => {
              field.onChange(val);
              onNumberChange?.(val);
            }}
            onBlur={(e) => {
              field.onBlur();
              onBlur?.(e as any);
            }}
            className={clsx(inputClassName, styles.regularInput)}
          />
        </ConditionalRender>
      </div>
      <ConditionalRender visible={error?.message}>
        <FieldError message={error?.message} className={clsx(styles.error, errorClassName)} />
      </ConditionalRender>
    </div>
  );
};

export default InputField;
